import { createContext, useState, useContext } from "react";
import { isPriceAdjustmentRuleConfigured } from "../utils/priceAdjustmentModal";

export const ActionRuleContext = createContext();

const finiteNumberOrNull = (value) => {
     if (value === null || value === undefined || value === "") return null;
     const normalized = typeof value === "string" ? value.trim().replace(",", ".") : value;
     const parsed = Number(normalized);
     return Number.isFinite(parsed) ? parsed : null;
};

const finiteNumberOrZero = (value) => finiteNumberOrNull(value) ?? 0;

const selectValue = (value) => {
     if (value === null || value === undefined) return "";
     if (typeof value === "object") return String(value.value ?? value.label ?? "");
     return String(value);
};

const normalizeFormula = (formula) => ({
     ...(formula || {}),
     X: finiteNumberOrZero(formula?.X),
     Y: finiteNumberOrZero(formula?.Y),
     Z: finiteNumberOrZero(formula?.Z)
});

const normalizeAdjustmentActions = (actions) => {
     if (!Array.isArray(actions)) return [];
     return actions.map((action) => ({
          ...action,
          ...(Object.prototype.hasOwnProperty.call(action || {}, "priceUntil")
               ? { priceUntil: finiteNumberOrNull(action.priceUntil) }
               : {}),
          ...(Object.prototype.hasOwnProperty.call(action || {}, "underBidAmount")
               ? { underBidAmount: finiteNumberOrNull(action.underBidAmount) }
               : {})
     }));
};

const cloneDraftDataFallback = (value) => {
     if (Array.isArray(value)) {
          return value.map(cloneDraftDataFallback);
     }
     if (value && typeof value === "object") {
          return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, cloneDraftDataFallback(entry)]));
     }
     return value;
};

const cloneDraftData = (value) => {
     if (typeof structuredClone === "function") {
          try {
               return structuredClone(value);
          } catch {
               return cloneDraftDataFallback(value);
          }
     }
     return cloneDraftDataFallback(value);
};

/** @typedef {Object} PriceChangeMarginHelper
 * @property {(active: boolean) => void} toggle
 * @property {() => boolean} isActive
 * @property {(val: string) => void} setMargeType
 * @property {() => string} getMargeType
 * @property {(val: number) => void} setNettoBruttoValue
 * @property {() => number} getNettoBruttoValue
 * @property {(val: string) => void} setMargeFall
 * @property {() => string} getMargeFall
 * @property {(val: number) => void} setMinimumEuroOverEK
 * @property {() => number} getMinimumEuroOverEK
 * @property {(val: number) => void} setMinimumPercent
 * @property {() => number} getMinimumPercent
 * @property {(val: string) => void} setMinimumPriceFormulaBase
 * @property {() => string} getMinimumPriceFormulaBase
 * @property {(val: boolean) => void} setMinimumPriceFormulaAddTax
 * @property {() => boolean} getMinimumPriceFormulaAddTax
 * @property {(val: Object) => void} setFormula
 * @property {() => Object} getFormula
 * @property {() => boolean} isConfigured
 * @property {() => void} clear
 */

/** @typedef {Object} CalculationChannelAndRuleHelper
 * @property {(active: boolean) => void} toggle
 * @property {() => boolean} isActive
 * @property {(val: number) => void} setChannelId
 * @property {() => number} getChannelId
 * @property {(val: number) => void} setRuleId
 * @property {() => number} getRuleId
 * @property {() => boolean} isConfigured
 */

/** @typedef {Object} RoundPriceHelper
 * @property {(active: boolean) => void} toggle
 * @property {() => boolean} isActive
 * @property {(val: string) => void} setRoundingType
 * @property {() => string} getRoundingType
 * @property {(val: Object) => void} setRoundingValues
 * @property {() => Object} getRoundingValues
 * @property {(val: boolean) => void} setContraryBackupRule
 * @property {() => boolean} getContraryBackupRule
 * @property {() => boolean} isConfigured
 */

/** @typedef {Object} ChangeIntervalHelper
 * @property {(active: boolean) => void} toggle
 * @property {() => boolean} isActive
 * @property {(val: string) => void} setDates
 * @property {() => string} getDates
 * @property {(val: string) => void} setTimePeriodTurnus
 * @property {() => string} getTimePeriodTurnus
 * @property {(val: string[]) => void} setWeekdays
 * @property {() => string[]} getWeekdays
 * @property {(val: string[]) => void} setMonthdays
 * @property {() => string[]} getMonthdays
 * @property {() => boolean} isConfigured
 */

/** @typedef {Object} PriceChangeHelper
 * @property {() => Object[]} getPriceAdjustmentRules
 * @property {(priceAdjustmentRule: Object) => string} addPriceAdjustmentRule
 * @property {(ruleId: string) => void} removePriceAdjustmentRule
 * @property {(ruleId: string, field: string, value: any) => void} updatePriceAdjustmentRule
 * @property {(ruleId: string) => Object|null} getPriceAdjustmentRule
 * @property {() => boolean} hasActivePriceAdjustmentRules
 * @property {(active: boolean) => void} toggle
 * @property {() => boolean} isActive
 * @property {() => boolean} getPriceChangeAsCoupon
 * @property {(val: boolean) => void} setPriceChangeAsCoupon
 * @property {() => boolean} getTypeOfPriceChangeInEuro
 * @property {(val: boolean) => void} setTypeOfPriceChangeInEuro
 * @property {() => Object|null} getComparisonOption
 * @property {(val: Object) => void} setComparisonOption
 * @property {() => Object[]} getAction
 * @property {(val: Object[]) => void} setAction
 * @property {() => boolean} isConfigured
 */

/** @typedef {Object} MaxPriceChangeHelper
 * @property {(active: boolean) => void} toggle
 * @property {() => boolean} isActive
 * @property {(val: number) => void} setMaxPriceDecreaseValue
 * @property {() => number} getMaxPriceDecreaseValue
 * @property {(val: number) => void} setMaxPriceIncreaseValue
 * @property {() => number} getMaxPriceIncreaseValue
 * @property {(val: number) => void} setMinimumEuroOverEK
 * @property {() => number} getMinimumEuroOverEK
 * @property {(val: number) => void} setMinimumPercent
 * @property {() => number} getMinimumPercent
 * @property {(val: number) => void} setMinFormulaBase
 * @property {() => number} getMinFormulaBase
 * @property {(val: number) => void} setMaxFormulaBase
 * @property {() => number} getMaxFormulaBase
 * @property {(val: number) => void} setMinFormulaAddTax
 * @property {() => number} getMinFormulaAddTax
 * @property {(val: number) => void} setMaxFormulaAddTax
 * @property {() => number} getMaxFormulaAddTax
 * @property {(val: Object) => void} setMinimumPrice
 * @property {() => Object} getMinimumPrice
 * @property {(val: Object) => void} setMaximumPrice
 * @property {() => Object} getMaximumPrice
 * @property {() => boolean} isConfigured
 */

/** @typedef {Object} ActionRuleHelper
 * @property {() => Object[]} getActiveRules
 */

/** @typedef {Object} ActionRuleContextValue
 * @property {PriceChangeMarginHelper} PriceChangeMarginHelper
 * @property {CalculationChannelAndRuleHelper} CalculationChannelAndRuleHelper
 * @property {RoundPriceHelper} RoundPriceHelper
 * @property {ChangeIntervalHelper} ChangeIntervalHelper
 * @property {PriceChangeHelper} PriceChangeHelper
 * @property {MaxPriceChangeHelper} MaxPriceChangeHelper
 * @property {ActionRuleHelper} ActionRuleHelper
 */

export const ActionRuleContextProvider = ({ children }) => {
     const [actionRuleData, setActionRuleData] = useState({
          adjustmentByMarge: {
               keyString: "priceChangeMargin",
               name: "Preisanpassung nach Marge",
               active: false,
               margeType: "",
               nettoBruttoValue: -1,
               MargeFall: "",
               minimumBoundaryEnabled: false,
               minimumEuroOverEK: null,
               minimumPercent: null,
               minimumPriceFormulaBase: "",
               minimumPriceFormulaAddTax: false,
               formula: {
                    OP_1: null,
                    X: 0,
                    OP_2: null,
                    Y: 0,
                    OP_3: null,
                    Z: 0,
                    useFactor: true
               }
          },

          calculate_by_channel: {
               keyString: "calculationChannelAndRulePrice",
               name: "Kalkulation auf Kanal- und Regel-Preis",
               active: false,
               channel_id: null,
               rule_id: null,
          },
          priceRounding: {
               keyString: "roundPrice",
               name: "Preisrundungen",
               active: false,
               roundingType: "",
               roundingValues: {},
               activateContraryBackupRuleRoundMode: false,
          },
          dateComponent: {
               keyString: "changeInterval",
               name: "Anpassungs-Intervall",
               active: false,
               dates: "",
               timePeriodTurnus: "",
               weekdays: [],
               monthdays: []
          },
          priceAdjustmentRules: [],
          maxPriceAdjustment: {
               keyString: "maxPriceAdjustment",
               name: "Maximale Preisangleichung",
               active: false,
               maxPriceDecreaseValue: 0,
               maxPriceIncreaseValue: 0,
               minimumBoundaryEnabled: false,
               minimumEuroOverEK: null,
               minimumPercent: null,
               minimumPriceFormulaBase: "",
               maximumPriceFormulaBase: "",
               minimumPriceFormulaAddTax: false,
               maximumPriceFormulaAddTax: false,
               minimumPrice: {
                    OP_1: null,
                    X: 0,
                    OP_2: null,
                    Y: 0,
                    OP_3: null,
                    Z: 0,
                    useFactor: false
               },
               maximumPrice: {
                    OP_1: null,
                    X: 0,
                    OP_2: null,
                    Y: 0,
                    OP_3: null,
                    Z: 0,
                    useFactor: false
               }
          },
          amazonBuyBox: {
               keyString: "amazonBuyBox",
               name: "Amazon BuyBox",
               active: false
          }
     });

     const [activePriceAdjustmentRuleId, setActivePriceAdjustmentRuleId] = useState(null);

     const updateField = (section, field, value) => {
          setActionRuleData((prev) => ({
               ...prev,
               [section]: {
                    ...prev[section],
                    [field]: value
               }
          }));
     };

     const generatePriceAdjustmentRuleId = () => `price_adjustment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

     const resolvePriceAdjustmentRuleId = () => {
          if (activePriceAdjustmentRuleId) {
               const exists = actionRuleData.priceAdjustmentRules.some((rule) => rule.id === activePriceAdjustmentRuleId);
               if (exists) return activePriceAdjustmentRuleId;
          }
          return actionRuleData.priceAdjustmentRules[0]?.id ?? null;
     };

     const addPriceAdjustmentRule = (priceAdjustmentRule = {}) => {
          const ruleId = generatePriceAdjustmentRuleId();
          const newPriceAdjustmentRule = {
               id: ruleId,
               keyString: "priceChange",
               name: "Preisangleichung",
               active: false,
               priceChangeAsCoupon: false,
               typeOfPriceChangeInEuro: false,
                ComparisonOption: null,
                action: [],
                foreignShopStockRuleReference: null,
                ...priceAdjustmentRule
          };
          newPriceAdjustmentRule.action = normalizeAdjustmentActions(newPriceAdjustmentRule.action);

          setActionRuleData(prev => ({
               ...prev,
               priceAdjustmentRules: [...prev.priceAdjustmentRules, newPriceAdjustmentRule]
          }));
          setActivePriceAdjustmentRuleId(ruleId);

          return ruleId;
     };

     const removePriceAdjustmentRule = (ruleId) => {
          setActionRuleData(prev => ({
               ...prev,
               priceAdjustmentRules: prev.priceAdjustmentRules.filter(rule => rule.id !== ruleId)
          }));
          setActivePriceAdjustmentRuleId((currentId) => {
               if (currentId !== ruleId) return currentId;
               const remaining = actionRuleData.priceAdjustmentRules.filter((rule) => rule.id !== ruleId);
               return remaining[0]?.id ?? null;
          });
     };

     const updatePriceAdjustmentRule = (ruleId, field, value) => {
          const normalizedValue = field === "action" ? normalizeAdjustmentActions(value) : value;
          setActionRuleData(prev => ({
               ...prev,
               priceAdjustmentRules: prev.priceAdjustmentRules.map(rule =>
                    rule.id === ruleId ? { ...rule, [field]: normalizedValue } : rule
               )
          }));
     };

     const getPriceAdjustmentRule = (ruleId) => {
          return actionRuleData.priceAdjustmentRules.find(rule => rule.id === ruleId) || null;
     };

     return (
          <ActionRuleContext.Provider
               value={{
                    PriceChangeMarginHelper: {
                         toggle: (val) => updateField("adjustmentByMarge", "active", val),
                         isActive: () => actionRuleData.adjustmentByMarge.active,
                         setMargeType: (val) => updateField("adjustmentByMarge", "margeType", val),
                         getMargeType: () => actionRuleData.adjustmentByMarge.margeType,
                         setNettoBruttoValue: (val) => updateField("adjustmentByMarge", "nettoBruttoValue", finiteNumberOrZero(val)),
                         getNettoBruttoValue: () => actionRuleData.adjustmentByMarge.nettoBruttoValue,
                         setMargeFall: (val) => updateField("adjustmentByMarge", "MargeFall", val),
                         getMargeFall: () => actionRuleData.adjustmentByMarge.MargeFall,
                         setMinimumBoundaryEnabled: (enabled) => {
                              setActionRuleData((prev) => ({
                                   ...prev,
                                   adjustmentByMarge: {
                                        ...prev.adjustmentByMarge,
                                        minimumBoundaryEnabled: enabled,
                                        minimumEuroOverEK: enabled
                                             ? (prev.adjustmentByMarge.minimumEuroOverEK ?? 0)
                                             : null,
                                        minimumPercent: enabled
                                             ? (prev.adjustmentByMarge.minimumPercent ?? 0)
                                             : null,
                                   }
                              }));
                         },
                         isMinimumBoundaryEnabled: () => !!actionRuleData.adjustmentByMarge.minimumBoundaryEnabled,
                         setMinimumEuroOverEK: (val) => updateField("adjustmentByMarge", "minimumEuroOverEK", finiteNumberOrNull(val)),
                         getMinimumEuroOverEK: () => actionRuleData.adjustmentByMarge.minimumEuroOverEK,
                         setMinimumPercent: (val) => updateField("adjustmentByMarge", "minimumPercent", finiteNumberOrNull(val)),
                         getMinimumPercent: () => actionRuleData.adjustmentByMarge.minimumPercent,
                         setMinimumPriceFormulaBase: (val) => updateField("adjustmentByMarge", "minimumPriceFormulaBase", selectValue(val)),
                         getMinimumPriceFormulaBase: () => actionRuleData.adjustmentByMarge.minimumPriceFormulaBase,
                         setMinimumPriceFormulaAddTax: (val) => updateField("adjustmentByMarge", "minimumPriceFormulaAddTax", val),
                         getMinimumPriceFormulaAddTax: () => actionRuleData.adjustmentByMarge.minimumPriceFormulaAddTax,
                         setFormula: (val) => updateField("adjustmentByMarge", "formula", normalizeFormula(val)),
                         getFormula: () => actionRuleData.adjustmentByMarge.formula,
                         isConfigured: () => {
                              const data = actionRuleData.adjustmentByMarge;
                              const validFall = ["ALL_PRODUCTS", "ONLY_LESSER", "ONLY_GREATER"];
                              const validType = ["FORMEL", "NETTOFAKTOR", "BRUTTOFAKTOR"];
                              return validFall.includes(data.MargeFall) && validType.includes(data.margeType);
                         },
                         clear: () => {
                              setActionRuleData((prev) => ({
                                   ...prev,
                                   adjustmentByMarge: {
                                        ...prev.adjustmentByMarge,
                                        active: false,
                                        margeType: "",
                                        nettoBruttoValue: -1,
                                        MargeFall: "",
                                        minimumBoundaryEnabled: false,
                                        minimumEuroOverEK: null,
                                        minimumPercent: null,
                                        minimumPriceFormulaBase: "",
                                        minimumPriceFormulaAddTax: false,
                                        formula: {
                                             OP_1: null,
                                             X: 0,
                                             OP_2: null,
                                             Y: 0,
                                             OP_3: null,
                                             Z: 0,
                                             useFactor: true
                                        }
                                   }
                              }));
                         }
                    },
                    CalculationChannelAndRuleHelper: {
                         toggle: (val) => updateField("calculate_by_channel", "active", val),
                         isActive: () => actionRuleData.calculate_by_channel.active,
                         setChannelId: (val) => updateField("calculate_by_channel", "channel_id", val),
                         getChannelId: () => actionRuleData.calculate_by_channel.channel_id,
                         setRuleId: (val) => updateField("calculate_by_channel", "rule_id", val),
                         getRuleId: () => actionRuleData.calculate_by_channel.rule_id,
                         isConfigured: () => {
                              const data = actionRuleData.calculate_by_channel;
                              return !!data.channel_id || !!data.rule_id;
                         }
                    },
                    RoundPriceHelper: {
                         toggle: (val) => updateField("priceRounding", "active", val),
                         isActive: () => actionRuleData.priceRounding.active,
                         setRoundingType: (val) => updateField("priceRounding", "roundingType", val),
                         getRoundingType: () => actionRuleData.priceRounding.roundingType,
                         setRoundingValues: (val) => updateField("priceRounding", "roundingValues", val),
                         getRoundingValues: () => actionRuleData.priceRounding.roundingValues,
                         setContraryBackupRule: (val) => updateField("priceRounding", "activateContraryBackupRuleRoundMode", val),
                         getContraryBackupRule: () => actionRuleData.priceRounding.activateContraryBackupRuleRoundMode,
                         isConfigured: () => {
                              const data = actionRuleData.priceRounding;
                              const hasType = !!data.roundingType;
                              const hasValues = data.roundingValues && Object.keys(data.roundingValues).length > 0;
                              return hasType || hasValues;
                         }
                    },
                    ChangeIntervalHelper: {
                         toggle: (val) => updateField("dateComponent", "active", val),
                         isActive: () => actionRuleData.dateComponent.active,
                         setDates: (val) => updateField("dateComponent", "dates", val),
                         getDates: () => actionRuleData.dateComponent.dates,
                         setTimePeriodTurnus: (val) => updateField("dateComponent", "timePeriodTurnus", val),
                         getTimePeriodTurnus: () => actionRuleData.dateComponent.timePeriodTurnus,
                         setWeekdays: (val) => updateField("dateComponent", "weekdays", val),
                         getWeekdays: () => actionRuleData.dateComponent.weekdays,
                         setMonthdays: (val) => updateField("dateComponent", "monthdays", val),
                         getMonthdays: () => actionRuleData.dateComponent.monthdays,
                         isConfigured: () => {
                              const data = actionRuleData.dateComponent;
                              const hasDates = !!data.dates;
                              const hasTurnus = !!data.timePeriodTurnus;
                              const hasWeekdays = Array.isArray(data.weekdays) && data.weekdays.length > 0;
                              const hasMonthdays = Array.isArray(data.monthdays) && data.monthdays.length > 0;
                              return hasDates || hasTurnus || hasWeekdays || hasMonthdays;
                         }
                    },
                    PriceChangeHelper: {
                         getPriceAdjustmentRules: () => actionRuleData.priceAdjustmentRules,
                         addPriceAdjustmentRule,
                         removePriceAdjustmentRule,
                         updatePriceAdjustmentRule,
                         getPriceAdjustmentRule,
                         setActivePriceAdjustmentRuleId,
                         getActivePriceAdjustmentRuleId: () => resolvePriceAdjustmentRuleId(),
                         hasActivePriceAdjustmentRules: () =>
                              actionRuleData.priceAdjustmentRules.some(
                                   (rule) => rule.active && isPriceAdjustmentRuleConfigured(rule)
                              ),
                         toggle: (val) => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              if (ruleId) {
                                   updatePriceAdjustmentRule(ruleId, "active", val);
                              }
                         },
                         isActive: () => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              if (!ruleId) return false;
                              return !!actionRuleData.priceAdjustmentRules.find((rule) => rule.id === ruleId)?.active;
                         },
                         getPriceChangeAsCoupon: () => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              return ruleId
                                   ? actionRuleData.priceAdjustmentRules.find((rule) => rule.id === ruleId)?.priceChangeAsCoupon || false
                                   : false;
                         },
                         setPriceChangeAsCoupon: (val) => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              if (ruleId) updatePriceAdjustmentRule(ruleId, "priceChangeAsCoupon", val);
                         },
                         getTypeOfPriceChangeInEuro: () => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              return ruleId
                                   ? actionRuleData.priceAdjustmentRules.find((rule) => rule.id === ruleId)?.typeOfPriceChangeInEuro || false
                                   : false;
                         },
                         setTypeOfPriceChangeInEuro: (val) => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              if (ruleId) updatePriceAdjustmentRule(ruleId, "typeOfPriceChangeInEuro", val);
                         },
                         getComparisonOption: () => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              return ruleId
                                   ? actionRuleData.priceAdjustmentRules.find((rule) => rule.id === ruleId)?.ComparisonOption ?? null
                                   : null;
                         },
                         setComparisonOption: (val) => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              if (ruleId) updatePriceAdjustmentRule(ruleId, "ComparisonOption", val);
                         },
                         getAction: () => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              return ruleId
                                   ? actionRuleData.priceAdjustmentRules.find((rule) => rule.id === ruleId)?.action || []
                                   : [];
                         },
                         setAction: (val) => {
                              const ruleId = resolvePriceAdjustmentRuleId();
                              if (ruleId) updatePriceAdjustmentRule(ruleId, "action", normalizeAdjustmentActions(val));
                         },
                         isConfigured: () =>
                              actionRuleData.priceAdjustmentRules.some(
                                   (rule) => rule.active && isPriceAdjustmentRuleConfigured(rule)
                              )
                    },
                    MaxPriceChangeHelper: {
                         toggle: (val) => updateField("maxPriceAdjustment", "active", val),
                         isActive: () => actionRuleData.maxPriceAdjustment.active,
                         setMaxPriceDecreaseValue: (val) => updateField("maxPriceAdjustment", "maxPriceDecreaseValue", finiteNumberOrZero(val)),
                         getMaxPriceDecreaseValue: () => actionRuleData.maxPriceAdjustment.maxPriceDecreaseValue,
                         setMaxPriceIncreaseValue: (val) => updateField("maxPriceAdjustment", "maxPriceIncreaseValue", finiteNumberOrZero(val)),
                         getMaxPriceIncreaseValue: () => actionRuleData.maxPriceAdjustment.maxPriceIncreaseValue,
                         setMinimumBoundaryEnabled: (enabled) => {
                              setActionRuleData((prev) => ({
                                   ...prev,
                                   maxPriceAdjustment: {
                                        ...prev.maxPriceAdjustment,
                                        minimumBoundaryEnabled: enabled,
                                        minimumEuroOverEK: enabled
                                             ? (prev.maxPriceAdjustment.minimumEuroOverEK ?? 0)
                                             : null,
                                        minimumPercent: enabled
                                             ? (prev.maxPriceAdjustment.minimumPercent ?? 0)
                                             : null,
                                   }
                              }));
                         },
                         isMinimumBoundaryEnabled: () => !!actionRuleData.maxPriceAdjustment.minimumBoundaryEnabled,
                         setMinimumEuroOverEK: (val) => updateField("maxPriceAdjustment", "minimumEuroOverEK", finiteNumberOrNull(val)),
                         getMinimumEuroOverEK: () => actionRuleData.maxPriceAdjustment.minimumEuroOverEK,
                         setMinimumPercent: (val) => updateField("maxPriceAdjustment", "minimumPercent", finiteNumberOrNull(val)),
                         getMinimumPercent: () => actionRuleData.maxPriceAdjustment.minimumPercent,
                         setMinFormulaBase: (val) => updateField("maxPriceAdjustment", "minimumPriceFormulaBase", selectValue(val)),
                         getMinFormulaBase: () => actionRuleData.maxPriceAdjustment.minimumPriceFormulaBase,
                         setMaxFormulaBase: (val) => updateField("maxPriceAdjustment", "maximumPriceFormulaBase", selectValue(val)),
                         getMaxFormulaBase: () => actionRuleData.maxPriceAdjustment.maximumPriceFormulaBase,
                         setMinFormulaAddTax: (val) => updateField("maxPriceAdjustment", "minimumPriceFormulaAddTax", val),
                         getMinFormulaAddTax: () => actionRuleData.maxPriceAdjustment.minimumPriceFormulaAddTax,
                         setMaxFormulaAddTax: (val) => updateField("maxPriceAdjustment", "maximumPriceFormulaAddTax", val),
                         getMaxFormulaAddTax: () => actionRuleData.maxPriceAdjustment.maximumPriceFormulaAddTax,
                         setMinimumPrice: (val) => updateField("maxPriceAdjustment", "minimumPrice", normalizeFormula(val)),
                         getMinimumPrice: () => actionRuleData.maxPriceAdjustment.minimumPrice,
                         setMaximumPrice: (val) => updateField("maxPriceAdjustment", "maximumPrice", normalizeFormula(val)),
                         getMaximumPrice: () => actionRuleData.maxPriceAdjustment.maximumPrice,
                         isConfigured: () => {
                              const data = actionRuleData.maxPriceAdjustment;
                              return (
                                   data.maxPriceDecreaseValue !== 0 ||
                                   data.maxPriceIncreaseValue !== 0 ||
                                   data.minimumBoundaryEnabled ||
                                   (data.minimumEuroOverEK !== null && data.minimumEuroOverEK !== 0) ||
                                   (data.minimumPercent !== null && data.minimumPercent !== 0)
                              );
                         }
                    },
                    AmazonBuyBoxHelper: {
                         toggle: (val) => updateField("amazonBuyBox", "active", val),
                         isActive: () => actionRuleData.amazonBuyBox.active,
                         isConfigured: () => actionRuleData.amazonBuyBox.active
                    },
                    ActionRuleHelper: {
                         createDraftSnapshot: () => ({
                              actionRuleData: cloneDraftData(actionRuleData),
                              activePriceAdjustmentRuleId
                         }),
                         restoreDraftSnapshot: (snapshot) => {
                              if (!snapshot) return;
                              setActionRuleData(cloneDraftData(snapshot.actionRuleData));
                              setActivePriceAdjustmentRuleId(snapshot.activePriceAdjustmentRuleId);
                         },
                         getActiveRules: () => {
                              const activeRules = Object.values(actionRuleData).filter((data) => data.active && Array.isArray(data) === false);
                              const activePriceAdjustmentRules = actionRuleData.priceAdjustmentRules.filter(
                                   (rule) => rule.active && isPriceAdjustmentRuleConfigured(rule)
                              );
                              return [...activeRules, ...activePriceAdjustmentRules];
                         }
                    }
               }}
          >
               {children}
          </ActionRuleContext.Provider>
     );
};

/** @returns {ActionRuleContextValue} */
export const useActionRule = () => {
     const context = useContext(ActionRuleContext);
     if (!context) {
          throw new Error("useActionRule muss innerhalb des ActionRuleContextProviders verwendet werden");
     }
     return context;
};